/**
 * Coach artifact payloads, mirroring api/coach/artifacts/schema.py.
 *
 * Nothing here is generated: the two clients and the backend share no types
 * (design spec risk R6). The mitigation is this file plus `v` in the envelope
 * plus the tolerance rules below.
 *
 * ── R5b ─────────────────────────────────────────────────────────────────
 * Design spec §3.2: "Clients MUST ignore artifacts with an unknown `type` or a
 * `v` they don't understand, rendering nothing rather than erroring. This is
 * what lets either client add a type later without a backend change."
 *
 * `decodeArtifact` is the only gate. It returns `null` — never throws, never
 * partially-constructs — for an unknown type, an unrecognised version, a
 * malformed envelope, or a payload missing a field its renderer needs.
 * Rows/sets/entries inside an otherwise-valid payload are dropped individually:
 * one bad row must not cost the user the whole card.
 *
 * ── All six registered types ────────────────────────────────────────────
 * As of Spec I's Task 2 this client decodes every type the backend registry
 * emits (`api/coach/artifacts/registry.py`): `session`, `record`,
 * `progression`, `exercise`, `video`, `muscles`.
 *
 * That does NOT make the tolerance below redundant — see the `default` branch of
 * `decodeArtifact`. A widget owns its decoder branch (design §3), so a seventh
 * backend type arrives here with nothing to decode it and must draw nothing
 * rather than throw. KNOWN_UNRENDERED in __tests__/artifactContract.test.tsx is
 * where such a gap is declared; it is empty today.
 */

/** Bumped by the backend only when a payload changes incompatibly. */
export const ARTIFACT_VERSION = 1

// ── session ──────────────────────────────────────────────────────────

export interface SessionArtifactRow {
  name: string
  target_weight_kg: number | null
}

export interface SessionArtifactBlock {
  type: 'strength' | 'cardio' | 'mobility'
  label: string
  detail: string | null
  rows: SessionArtifactRow[]
}

export interface SessionArtifactData {
  title: string
  recency_label: string | null
  recency_tone: 'neutral' | 'warn'
  rows: SessionArtifactRow[]
  blocks: SessionArtifactBlock[]
  href: string | null
}

// ── record ───────────────────────────────────────────────────────────

export interface RecordArtifactSet {
  index: number
  // api/coach/artifacts/schema.py's SetRow.reps is nullable since the
  // backend's Task 4 (performed_sets() now includes timed sets, which
  // carry no reps) -- this mirrors that. decodeRecord below admits such a
  // row as of Plan D: a set needs a value on whichever dimension scores it,
  // not on reps specifically.
  reps: number | null
  weight_kg: number
  // Sent by api/coach/artifacts/resolvers/record.py as of Plan D Task 1.
  // Still optional: a cached transcript or a rolled-back deploy can carry a
  // payload without them, and `metric` then defaults to 'reps'.
  metric?: 'reps' | 'seconds'
  duration_sec?: number | null
}

export interface RecordArtifactData {
  exercise: string
  /** ISO-8601 date, as emitted. Formatted for display by `formatArtifactDate`. */
  date: string
  sets: RecordArtifactSet[]
  /**
   * The verdict word, e.g. `LOAD UP`. Rendered as a plain chip.
   *
   * There is deliberately no `trend_pct` beside it: the field was dropped from
   * the backend payload on 2026-08-07 (design §10) because it was hardcoded
   * `None` and its chip could never fire. That is why this card does not use
   * the shared `TrendBadge` — `TrendVerdict.pct` is required and non-nullable,
   * so there is nothing to build one from. The known, accepted cost is that
   * this card shows a word where the Sessions view shows a percentage.
   */
  trend_label: string | null
  trend_direction: 'up' | 'down' | 'flat' | null
  href: string | null
}

// ── progression ──────────────────────────────────────────────────────

export interface ProgressionPoint {
  /** ISO-8601 date, as emitted. */
  date: string
  value: number
}

export interface ProgressionArtifactData {
  exercise: string
  /** Emitted per movement; `kg` for weight, but the backend may send `reps`. */
  unit: string
  /** Already-composed eyebrow, e.g. `Top set · 12 weeks`. */
  label: string
  points: ProgressionPoint[]
}

// ── exercise ─────────────────────────────────────────────────────────

export interface ExerciseArtifactData {
  name: string
  image_url: string | null
  /** Display labels, primary muscles first. */
  muscles: string[]
  /** How many leading entries of `muscles` are primary. */
  primary_count: number
  href: string | null
}

// ── video ────────────────────────────────────────────────────────────

export interface VideoArtifactData {
  title: string
  /** Fully qualified `/media/...` as stored; prefix BASE_URL and nothing else. */
  poster_url: string | null
  media_url: string
  href: string | null
  /**
   * There is deliberately no `duration_seconds`. The field was dropped from the
   * backend payload on 2026-08-08 because it was hardcoded `None` on both
   * branches of the video resolver, so the mockup's `0:42` badge could never
   * fire — the same call `trend_pct` got on the record card. Do not add a key
   * for it here: `extra="forbid"` server-side means nothing can ever send one,
   * so a decoder branch would be unreachable and a badge would be dead code.
   */
}

// ── muscles ──────────────────────────────────────────────────────────

export interface MuscleArtifactEntry {
  slug: string
  label: string
}

export interface MusclesArtifactData {
  exercise: string
  primary: MuscleArtifactEntry[]
  secondary: MuscleArtifactEntry[]
  /** Already folded through `muscle_map.highlight_slugs` server-side. */
  highlight_primary: string[]
  highlight_secondary: string[]
}

// ── envelope ─────────────────────────────────────────────────────────

/**
 * Where the card goes: under this 0-based paragraph of the reply, or `null` for
 * "after everything", which is where every card sat before Spec K Task 5.
 *
 * Resolved server-side from a quote the card-selection pass returns, and split
 * by `paragraphs()` in `api/coach/card_selection.py` — `splitParagraphs` in
 * `coach/anchoring.ts` MUST split identically or every index is one off. It is a
 * paragraph rather than a character offset because the reply is markdown, and
 * an offset can cut a list or an inline `**bold**` span in half.
 *
 * Optional on the wire: absent (an older backend, a cached transcript) reads the
 * same as `null`.
 */
interface Anchored {
  id: string
  v: number
  after_paragraph: number | null
}

export type CoachArtifact =
  | (Anchored & { type: 'session'; data: SessionArtifactData })
  | (Anchored & { type: 'record'; data: RecordArtifactData })
  | (Anchored & { type: 'progression'; data: ProgressionArtifactData })
  | (Anchored & { type: 'exercise'; data: ExerciseArtifactData })
  | (Anchored & { type: 'video'; data: VideoArtifactData })
  | (Anchored & { type: 'muscles'; data: MusclesArtifactData })

// ── decoding ─────────────────────────────────────────────────────────

type Json = Record<string, unknown>

function isObj(v: unknown): v is Json {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function list(v: unknown): unknown[] {
  return Array.isArray(v) ? v : []
}

function strings(v: unknown): string[] {
  return list(v).filter((s): s is string => typeof s === 'string')
}

function decodeSessionRows(raw: unknown): SessionArtifactRow[] {
  const rows: SessionArtifactRow[] = []
  for (const item of list(raw)) {
    if (!isObj(item)) continue
    const name = str(item.name)
    if (name === null) continue
    rows.push({ name, target_weight_kg: num(item.target_weight_kg) })
  }
  return rows
}

function decodeSession(d: Json): SessionArtifactData | null {
  const title = str(d.title)
  if (title === null) return null
  const blocks: SessionArtifactBlock[] = []
  for (const raw of list(d.blocks)) {
    if (!isObj(raw)) continue
    const type = str(raw.type)
    if (type !== 'strength' && type !== 'cardio' && type !== 'mobility') continue
    blocks.push({
      type,
      label: str(raw.label) ?? '',
      detail: str(raw.detail),
      rows: decodeSessionRows(raw.rows),
    })
  }
  return {
    title,
    recency_label: str(d.recency_label),
    recency_tone: d.recency_tone === 'warn' ? 'warn' : 'neutral',
    rows: decodeSessionRows(d.rows),
    blocks,
    href: str(d.href),
  }
}

function decodeRecord(d: Json): RecordArtifactData | null {
  const exercise = str(d.exercise)
  const date = str(d.date)
  if (exercise === null || date === null) return null
  const sets: RecordArtifactSet[] = []
  for (const raw of list(d.sets)) {
    if (!isObj(raw)) continue
    const index = num(raw.index)
    const reps = num(raw.reps)
    const weight = num(raw.weight_kg)
    const durationSec = num(raw.duration_sec)
    const metric = raw.metric === 'seconds' ? ('seconds' as const) : ('reps' as const)
    // A row needs a value on the dimension that SCORES it, not on reps
    // specifically. Requiring reps here is what dropped every hold before it
    // reached the card: the server sends one, the card can render one, and
    // this decoder threw it away in between.
    if (index === null || weight === null) continue
    if (reps === null && durationSec === null) continue
    sets.push({ index, reps, weight_kg: weight, duration_sec: durationSec, metric })
  }
  const dir = d.trend_direction
  // Built field by field, so a `trend_pct` still on the wire from a rolled-back
  // deploy or a cached transcript is dropped here rather than reaching a
  // renderer that has nowhere to put it.
  return {
    exercise,
    date,
    sets,
    trend_label: str(d.trend_label),
    trend_direction: dir === 'up' || dir === 'down' || dir === 'flat' ? dir : null,
    href: str(d.href),
  }
}

function decodeProgression(d: Json): ProgressionArtifactData | null {
  const exercise = str(d.exercise)
  const label = str(d.label)
  if (exercise === null || label === null) return null
  const points: ProgressionPoint[] = []
  for (const raw of list(d.points)) {
    if (!isObj(raw)) continue
    const date = str(raw.date)
    const value = num(raw.value)
    if (date === null || value === null) continue
    points.push({ date, value })
  }
  // A card with too few points to draw still decodes; the renderer decides. The
  // backend already declines under `_MIN_POINTS` (resolvers/progression.py), so
  // rejecting here as well would put the same rule in two places.
  return { exercise, unit: str(d.unit) ?? 'kg', label, points }
}

function decodeExercise(d: Json): ExerciseArtifactData | null {
  const name = str(d.name)
  if (name === null) return null
  return {
    name,
    image_url: str(d.image_url),
    muscles: strings(d.muscles),
    primary_count: num(d.primary_count) ?? 0,
    href: str(d.href),
  }
}

function decodeVideo(d: Json): VideoArtifactData | null {
  const title = str(d.title)
  const mediaUrl = str(d.media_url)
  // Both required: a video card with nothing to play, or nothing to name it, is
  // worse than no card.
  if (title === null || mediaUrl === null) return null
  return {
    title,
    poster_url: str(d.poster_url),
    media_url: mediaUrl,
    href: str(d.href),
  }
}

function decodeMuscleEntries(v: unknown): MuscleArtifactEntry[] {
  const out: MuscleArtifactEntry[] = []
  for (const raw of list(v)) {
    if (!isObj(raw)) continue
    const slug = str(raw.slug)
    const label = str(raw.label)
    if (slug === null || label === null) continue
    out.push({ slug, label })
  }
  return out
}

function decodeMuscles(d: Json): MusclesArtifactData | null {
  const exercise = str(d.exercise)
  if (exercise === null) return null
  return {
    exercise,
    primary: decodeMuscleEntries(d.primary),
    secondary: decodeMuscleEntries(d.secondary),
    highlight_primary: strings(d.highlight_primary),
    highlight_secondary: strings(d.highlight_secondary),
  }
}

/**
 * Turn one wire envelope into a typed artifact, or `null`.
 *
 * `null` means "render nothing" — never "throw", never "render a placeholder".
 * The caller drops it silently; that is what R5b requires.
 */
export function decodeArtifact(raw: unknown): CoachArtifact | null {
  if (!isObj(raw)) return null
  const id = str(raw.id)
  const type = str(raw.type)
  if (id === null || type === null) return null
  if (num(raw.v) !== ARTIFACT_VERSION) return null
  if (!isObj(raw.data)) return null

  const v = ARTIFACT_VERSION
  const d = raw.data
  // Absent, null, or anything that is not a whole non-negative number all mean
  // the same thing: no position, render at the end. Never a reason to drop a
  // card — a missing anchor must cost the placement and nothing else.
  const p = num(raw.after_paragraph)
  const after_paragraph = p !== null && Number.isInteger(p) && p >= 0 ? p : null

  switch (type) {
    case 'session': {
      const data = decodeSession(d)
      return data === null ? null : { id, v, after_paragraph, type: 'session', data }
    }
    case 'record': {
      const data = decodeRecord(d)
      return data === null ? null : { id, v, after_paragraph, type: 'record', data }
    }
    case 'progression': {
      const data = decodeProgression(d)
      return data === null ? null : { id, v, after_paragraph, type: 'progression', data }
    }
    case 'exercise': {
      const data = decodeExercise(d)
      return data === null ? null : { id, v, after_paragraph, type: 'exercise', data }
    }
    case 'video': {
      const data = decodeVideo(d)
      return data === null ? null : { id, v, after_paragraph, type: 'video', data }
    }
    case 'muscles': {
      const data = decodeMuscles(d)
      return data === null ? null : { id, v, after_paragraph, type: 'muscles', data }
    }
    default:
      // Every registered type now has a case above, so nothing the backend
      // currently emits reaches this branch. **It still must not be deleted.**
      // Design §4.1 is what lets the backend ship a widget before either client
      // knows about it — the day a seventh type is registered, this branch is
      // the difference between an old client drawing nothing and an old client
      // throwing mid-stream. R5b names it as the first gate.
      return null
  }
}

/**
 * Decode a stored `artifacts` list from the transcript. Returns `undefined`
 * rather than `[]` so `Message.artifacts` stays absent when there is nothing
 * to render, which keeps `MessageBubble`'s guard a single truthiness check.
 */
export function decodeArtifacts(raw: unknown): CoachArtifact[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const out: CoachArtifact[] = []
  for (const item of raw) {
    const artifact = decodeArtifact(item)
    if (artifact !== null) out.push(artifact)
  }
  return out.length > 0 ? out : undefined
}
